import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import React, { useCallback, useState, useEffect } from 'react';
import { useFormikContext } from 'formik';
import { css } from 'styled-components';
import Input from '../Input/Input';
import Fieldset from '../../Fieldset';
import TypeaheadInput from '../TypeaheadInput';
import { InputError } from '../Error';
import { accessPropertyByDotNotationString, toAlphanumericDashString, setPropertyByDotNotationString } from '../../../../utils';
import { grid, gridColSpanByBreakpoint, gridColSpan, SPACINGS } from '../../../../styling';
import { IS_DEV, IS_STAGING, IS_TESTING } from '../../../../constants';
import { Div } from '../../../Styled';
var ADDRESS_FIELDS = {
  street1: "street1",
  street2: "street2",
  city: "city",
  state: "state",
  zip: "zip"
};
var GRID_GAP_PX = 16;

var AddressInput = function AddressInput(_ref) {
  var legend = _ref.legend,
      _ref$formikModelPath = _ref.formikModelPath,
      formikModelPath = _ref$formikModelPath === void 0 ? 'address' : _ref$formikModelPath,
      $css = _ref.$css,
      collapseError = _ref.collapseError,
      className = _ref.className,
      _ref$disableTypeahead = _ref.disableTypeahead,
      disableTypeahead = _ref$disableTypeahead === void 0 ? false : _ref$disableTypeahead;

  var _useState = useState(''),
      _useState2 = _slicedToArray(_useState, 2),
      eligibleStatesErrorText = _useState2[0],
      setEligibleStatesErrorText = _useState2[1];

  var _useState3 = useState(disableTypeahead),
      _useState4 = _slicedToArray(_useState3, 2),
      isDisabledTypeahead = _useState4[0],
      setIsDisableTypeahead = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      networkError = _useState6[0],
      setNetworkError = _useState6[1];

  var _useFormikContext = useFormikContext(),
      setValues = _useFormikContext.setValues,
      values = _useFormikContext.values,
      errors = _useFormikContext.errors;

  var typeaheadName = "".concat(formikModelPath, ".").concat(ADDRESS_FIELDS.street1);
  var onAddressSelected = useCallback(function (address) {
    setPropertyByDotNotationString(values, formikModelPath, Object.keys(ADDRESS_FIELDS).reduce(function (fields, fieldKey) {
      if (fieldKey === 'fullAddress') return fields;
      return _extends({}, fields, _defineProperty({}, fieldKey, address[fieldKey] || ''));
    }, {}));
    setValues(_extends({}, values));
  }, [formikModelPath, setValues, values]);
  useEffect(function () {
    var err = '';
    [ADDRESS_FIELDS.zip, ADDRESS_FIELDS.state].forEach(function (name) {
      var _accessPropertyByDotN, _accessPropertyByDotN2;

      var value = (_accessPropertyByDotN = accessPropertyByDotNotationString(values, formikModelPath)) === null || _accessPropertyByDotN === void 0 ? void 0 : _accessPropertyByDotN[name];
      var error = (_accessPropertyByDotN2 = accessPropertyByDotNotationString(errors, formikModelPath)) === null || _accessPropertyByDotN2 === void 0 ? void 0 : _accessPropertyByDotN2[name];
      if (value && error && error.includes('eligible')) err = error;
    });
    setEligibleStatesErrorText(err);
  }, [errors, values, formikModelPath]);
  useEffect(function () {
    var typeaheadDisabledState = networkError ? true : disableTypeahead;
    setIsDisableTypeahead(typeaheadDisabledState);
  }, [networkError, disableTypeahead]);
  return /*#__PURE__*/React.createElement(Fieldset, {
    legend: legend,
    $css: css([".spacer{margin-bottom:", "px;}", ""], SPACINGS.X2L, $css),
    className: className
  }, /*#__PURE__*/React.createElement(Div, {
    $css: css(["", ""], grid({
      gap: GRID_GAP_PX
    }))
  }, !isDisabledTypeahead && /*#__PURE__*/React.createElement(TypeaheadInput, {
    name: typeaheadName,
    placeholder: "Search for address",
    url: "https://".concat(IS_DEV || IS_STAGING || IS_TESTING ? 'test' : 'www', ".figure.com/address-lookup?partialAddress="),
    onNetworkError: function onNetworkError() {
      // TODO khigh report an aria live error
      setNetworkError(true);
    },
    autoComplete: "new-address-line1",
    autoCorrect: "off",
    autoCapitalize: "off",
    spellCheck: "false",
    $css: gridColSpanByBreakpoint([12, 8])
  }, function (_ref2) {
    var listboxData = _ref2.listboxData,
        listboxId = _ref2.listboxId,
        activeListboxIndex = _ref2.activeListboxIndex;
    return /*#__PURE__*/React.createElement(React.Fragment, null, listboxData.filter(function (_ref3) {
      var fullAddress = _ref3.fullAddress,
          street1 = _ref3.street1,
          city = _ref3.city,
          state = _ref3.state,
          zip = _ref3.zip;
      return fullAddress && street1 && city && state && zip;
    }).map(function (address, index) {
      return /*#__PURE__*/React.createElement("li", {
        role: "option",
        "aria-selected": activeListboxIndex === index ? 'true' : 'false',
        id: "".concat(listboxId, "-").concat(index),
        "data-test-id": "".concat(listboxId, "-").concat(index),
        key: toAlphanumericDashString("".concat(address.fullAddress, "-").concat(index)),
        onClick: function onClick() {
          return onAddressSelected(address);
        } // suppress no keyboard a11y error.. we handle keyboard in Typeahead container
        ,
        onKeyDown: function onKeyDown() {
          return null;
        }
      }, address.fullAddress);
    }));
  }), isDisabledTypeahead && /*#__PURE__*/React.createElement(Input, {
    name: typeaheadName,
    placeholder: "Enter your address",
    autoComplete: "address-line1",
    autoCorrect: "off",
    autoCapitalize: "off",
    spellCheck: "false",
    $css: gridColSpanByBreakpoint([12, 8])
  }), /*#__PURE__*/React.createElement(Input, {
    name: "".concat(formikModelPath, ".").concat(ADDRESS_FIELDS.street2),
    placeholder: "Apt, suite, unit",
    autoComplete: "".concat(isDisabledTypeahead ? '' : 'new-', "address-line2"),
    autoCorrect: "off",
    autoCapitalize: "off",
    spellCheck: "false",
    $css: gridColSpanByBreakpoint([12, 4])
  }), /*#__PURE__*/React.createElement(Input, {
    name: "".concat(formikModelPath, ".").concat(ADDRESS_FIELDS.city),
    placeholder: "City",
    autoComplete: "".concat(isDisabledTypeahead ? '' : 'new-', "address-level2"),
    $css: gridColSpanByBreakpoint([12, 6])
  }), /*#__PURE__*/React.createElement(Input, {
    name: "".concat(formikModelPath, ".").concat(ADDRESS_FIELDS.state),
    placeholder: "State",
    autoComplete: "".concat(isDisabledTypeahead ? '' : 'new-', "address-level1"),
    $css: gridColSpanByBreakpoint([12, 3])
  }), /*#__PURE__*/React.createElement(Input, {
    name: "".concat(formikModelPath, ".").concat(ADDRESS_FIELDS.zip),
    placeholder: "Zipcode",
    autoComplete: "".concat(isDisabledTypeahead ? '' : 'new-', "postal-code"),
    $css: gridColSpanByBreakpoint([12, 3])
  }), eligibleStatesErrorText && /*#__PURE__*/React.createElement(InputError, {
    collapseError: collapseError,
    errorText: eligibleStatesErrorText,
    $css: css(["", " margin-top:-", "px;"], gridColSpan(12), GRID_GAP_PX)
  })));
};

export default AddressInput;